// src/features/user/logic.ts
// Validation, user admin helpers, formatters.
// Framework-agnostic — NO React imports.

import type { UserEditFormState } from './types';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ============================================
// Validation
// ============================================

export function validateUserUpdate(data: UserEditFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.email.trim()) errors.email = 'auth.emailRequired';
  else if (!EMAIL_RE.test(data.email)) errors.email = 'auth.emailInvalid';
  if (!data.username.trim()) errors.username = 'auth.usernameRequired';
  return errors;
}

// ============================================
// User status map
// ============================================

export const userStatusMap: Record<string, { labelKey: string; variant: string }> = {
  USER_STATUS_UNSPECIFIED: { labelKey: 'user.statusUnspecified', variant: 'secondary' },
  USER_STATUS_ACTIVE: { labelKey: 'user.statusActive', variant: 'success' },
  USER_STATUS_INACTIVE: { labelKey: 'user.statusInactive', variant: 'secondary' },
  USER_STATUS_SUSPENDED: { labelKey: 'user.statusSuspended', variant: 'warning' },
  USER_STATUS_BANNED: { labelKey: 'user.statusBanned', variant: 'error' },
  USER_STATUS_PENDING: { labelKey: 'user.statusPending', variant: 'info' },
  USER_STATUS_DELETED: { labelKey: 'user.statusDeleted', variant: 'error' },
};

export function formatUserStatus(status: string): { labelKey: string; variant: string } {
  return userStatusMap[status] ?? userStatusMap.USER_STATUS_UNSPECIFIED;
}

// ============================================
// Permission checks
// ============================================

export function canImpersonateUser(user: { status?: string }): boolean {
  return user.status === 'USER_STATUS_ACTIVE';
}

export function canResetMFA(user: { two_factor_enabled?: boolean; status?: string }): boolean {
  return !!user.two_factor_enabled && user.status === 'USER_STATUS_ACTIVE';
}

// ============================================
// Formatting
// ============================================

export function formatLastSeen(timestamp: string | undefined): string {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'user.lastSeenNow';
  if (diffMin < 60) return 'user.lastSeenMinutes';
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return 'user.lastSeenHours';
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return 'user.lastSeenDays';
  return 'user.lastSeenLong';
}

export function getLastSeenParams(timestamp: string | undefined): Record<string, string | number> {
  if (!timestamp) return {};
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 60) return { count: diffMin };
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return { count: diffHours };
  const diffDays = Math.floor(diffHours / 24);
  return { count: diffDays };
}
